import type { Card } from '@/engine/types';
import type { HandClass } from '../types';

const RANK_ORDER = '23456789TJQKA';

function rankIndex(r: string): number {
  const idx = RANK_ORDER.indexOf(r);
  if (idx < 0) throw new RangeError(`unknown rank: ${r}`);
  return idx;
}

/**
 * Convert a 2-card hole to its canonical 169-class identifier.
 * Pocket pair:  'AA' ... '22'
 * Suited:       'AKs', 'KJs', '76s' (higher rank first)
 * Offsuit:      'AKo', 'KJo', '76o' (higher rank first)
 */
export function handClass(hole: readonly [Card, Card]): HandClass {
  const [c0, c1] = hole;
  const r0 = c0.charAt(0);
  const s0 = c0.charAt(1);
  const r1 = c1.charAt(0);
  const s1 = c1.charAt(1);
  const i0 = rankIndex(r0);
  const i1 = rankIndex(r1);
  if (r0 === r1) return `${r0}${r0}`;
  const [hi, lo] = i0 > i1 ? [r0, r1] : [r1, r0];
  const suited = s0 === s1 ? 's' : 'o';
  return `${hi}${lo}${suited}`;
}

/** Enumerate every possible 169-class identifier exactly once. */
export function allHandClasses(): HandClass[] {
  const classes: HandClass[] = [];
  for (let i = RANK_ORDER.length - 1; i >= 0; i--) {
    const r = RANK_ORDER[i] as string;
    classes.push(`${r}${r}`); // pair
  }
  for (let i = RANK_ORDER.length - 1; i >= 0; i--) {
    for (let j = i - 1; j >= 0; j--) {
      const hi = RANK_ORDER[i] as string;
      const lo = RANK_ORDER[j] as string;
      classes.push(`${hi}${lo}s`);
      classes.push(`${hi}${lo}o`);
    }
  }
  return classes;
}
